import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, inr, shippingFor } from "@/lib/api";
import { useCart, usePageMeta, useSite } from "@/lib/store";
import { WRAP } from "@/components/store/Chrome";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { getSource } from "@/lib/source";
import { useAccountApi } from "@/lib/account";
import { Show } from "@clerk/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATES = ["Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh", "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand",
  "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"];

const F = ({ label, children, error }) => (
  <label className="block">
    <span className="label-caps text-[10px] text-[#666]">{label}</span>
    <div className="mt-1.5">{children}</div>
    {error && <span className="text-xs text-red mt-1 block">{error}</span>}
  </label>
);

function CouponBox({ coupon, setCoupon, email, items }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/coupons/check", { code: code.trim(), email: email.trim(), items });
      setCoupon(r.data);
      toast.success(`Coupon ${r.data.code} applied`);
    } catch (e) {
      toast.error(errMsg(e, "This coupon code isn't valid"));
    } finally {
      setBusy(false);
    }
  };
  if (coupon) {
    return (
      <div className="flex justify-between items-center text-sm font-arial bg-[#f3faf4] border border-[#cfe8d4] px-3 py-2 mb-3" data-testid="checkout-coupon-applied">
        <span><b>{coupon.code}</b> · {coupon.label}</span>
        <button type="button" onClick={() => setCoupon(null)} className="text-xs text-red underline" data-testid="checkout-coupon-remove">Remove</button>
      </div>
    );
  }
  return (
    <div className="flex gap-2 mb-3">
      <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
        placeholder="Coupon code" className="rounded-none bg-white h-10 font-arial" data-testid="checkout-coupon-input" />
      <button type="button" onClick={apply} disabled={busy || !code.trim()} className="px-4 border border-black text-xs font-bold tracking-[0.1em] hover:bg-black hover:text-white disabled:opacity-40 transition-colors" data-testid="checkout-coupon-apply">APPLY</button>
    </div>
  );
}

function AccountPrefill({ onLoad }) {
  const call = useAccountApi();
  useEffect(() => {
    call("get", "/me").then((r) => onLoad(r.data)).catch(() => {});
  }, [call]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function Checkout() {
  const cart = useCart();
  const { site } = useSite();
  const nav = useNavigate();
  usePageMeta(site?.pages?.checkout?.title, site?.pages?.checkout?.description, site?.pages?.checkout?.keywords);
  const region = "india";
  const [f, setF] = useState({ name: "", email: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const placed = useRef(false);
  const [terms, setTerms] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [same, setSame] = useState(true);
  const [me, setMe] = useState(null);
  const [saveAddr, setSaveAddr] = useState(true);
  const call = useAccountApi();
  const prefill = (m) => {
    setMe(m);
    const a = m.addresses.find((x) => x.default) || m.addresses[0];
    setF((cur) => ({ ...cur, name: cur.name || a?.name || m.name, email: m.email, phone: cur.phone || a?.phone || m.phone,
      ...(a && !cur.line1 ? { line1: a.line1, line2: a.line2, city: a.city, state: a.state, postal_code: a.postal_code } : {}) }));
  };
  const pickAddr = (a) => setF((cur) => ({ ...cur, name: a.name || cur.name, phone: a.phone || cur.phone, line1: a.line1, line2: a.line2, city: a.city, state: a.state, postal_code: a.postal_code }));
  const [b, setB] = useState({ name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "India" });
  if (!cart.items.length && !placed.current) return <Navigate to="/basket" replace />;

  const india = region === "india";
  const disc = coupon ? Math.min(coupon.discount, cart.subtotal) : 0;
  const ship = shippingFor(site?.shipping, india, cart.subtotal - disc);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = (k, props = {}) => <Input value={f[k]} onChange={set(k)} className="rounded-none bg-white h-11" data-testid={`checkout-${k}`} {...props} />;

  const binp = (k, props = {}) => <Input value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="rounded-none bg-white h-11" data-testid={`checkout-billing-${k}`} {...props} />;

  const validate = () => {
    const e = {};
    if (!same) {
      if (b.name.trim().length < 2) e.b_name = "Enter the billing name";
      if (b.line1.trim().length < 3) e.b_line1 = "Enter the billing address";
      if (b.city.trim().length < 2) e.b_city = "Enter the city";
      if (b.postal_code.trim().length < 3) e.b_postal_code = "Enter the PIN / postal code";
      if (b.country.trim().length < 2) e.b_country = "Enter the country";
    }
    if (f.name.trim().length < 2) e.name = "Enter your full name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) e.email = "Enter a valid email";
    if (f.phone.replace(/\D/g, "").length < (india ? 10 : 6)) e.phone = "Enter a valid phone number";
    if (f.line1.trim().length < 3) e.line1 = "Enter your address";
    if (f.city.trim().length < 2) e.city = "Enter your city";
    if (india && !f.state) e.state = "Choose your state";
    if (india && !/^[1-9][0-9]{5}$/.test(f.postal_code.trim())) e.postal_code = "Enter a valid 6-digit PIN code";
    if (!india && f.postal_code.trim().length < 2) e.postal_code = "Enter your postal code";
    if (!india && f.country.trim().length < 2) e.country = "Enter your country";
    if (!india && /^(india|in|bharat)$/i.test(f.country.trim())) e.country = "For India, choose 'India' above";
    if (!terms) e.terms = "Please accept the Terms and Conditions to continue";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const { name, email, phone, ...addr } = f;
      const r = await api.post("/orders", {
        customer: { name, email, phone },
        address: { ...addr, country: india ? "India" : addr.country, state: addr.state },
        billing_same: same,
        billing: same ? null : b,
        source: getSource(),
        items: cart.items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        accepted_terms: terms,
        coupon_code: coupon?.code || "",
      });
      placed.current = true;
      if (me && saveAddr) call("post", "/addresses", { address: { label: "", name, phone, line1: addr.line1, line2: addr.line2, city: addr.city, state: addr.state, postal_code: addr.postal_code, country: "India", default: false } }).catch(() => {});
      cart.clear();
      nav(`/pay/${r.data.id}?email=${encodeURIComponent(r.data.customer.email)}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not place the order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${WRAP} pt-12 max-w-5xl`}>
      <p className="label-caps text-[11px] text-red mb-1.5">{me ? "Checkout" : "Guest checkout"}</p>
      <h1 className="font-head font-extrabold text-4xl tracking-tight border-b-2 border-black pb-4">Checkout</h1>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10 mt-8" data-testid="checkout-form">
        <div className="space-y-8">
          <fieldset className="grid sm:grid-cols-2 gap-4">
            <legend className="font-head font-bold text-lg mb-3">Your details</legend>
            <div className="sm:col-span-2"><F label="Full name" error={errors.name}>{inp("name")}</F></div>
            <F label="Email" error={errors.email}>{inp("email", { type: "email", readOnly: !!me, title: me ? "Signed in with this email" : undefined })}</F>
            <F label="Phone" error={errors.phone}>{inp("phone", { type: "tel" })}</F>
          </fieldset>
          <fieldset className="grid sm:grid-cols-2 gap-4">
            <legend className="font-head font-bold text-lg mb-3">Delivery address</legend>
            <Show when="signed-in"><AccountPrefill onLoad={prefill} /></Show>
            <Show when="signed-out"><p className="sm:col-span-2 text-xs font-arial text-[#666]" data-testid="checkout-sign-in-hint">Have an account? <Link to="/sign-in" className="text-red underline">Sign in</Link> to fill in your saved address. Or <Link to="/sign-up" className="text-red underline">create an account</Link> to track all your orders.</p></Show>
            {me?.addresses?.length > 1 && (
              <div className="sm:col-span-2 flex flex-wrap gap-2" data-testid="checkout-saved-addresses">
                {me.addresses.map((a, i) => <button type="button" key={i} onClick={() => pickAddr(a)} className={`px-3 py-2 text-xs border font-arial ${f.line1 === a.line1 && f.postal_code === a.postal_code ? "border-black bg-black text-white" : "border-[#ccc] bg-white hover:border-black"}`} data-testid={`checkout-saved-address-${i}`}>{a.label || a.line1}, {a.city}</button>)}
              </div>
            )}
            <div className="sm:col-span-2"><F label="Address line 1" error={errors.line1}>{inp("line1")}</F></div>
            <div className="sm:col-span-2"><F label="Address line 2 (optional)">{inp("line2")}</F></div>
            <F label="City" error={errors.city}>{inp("city")}</F>
            {india ? (
              <F label="State" error={errors.state}>
                <Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}>
                  <SelectTrigger className="rounded-none bg-white h-11" data-testid="checkout-state"><SelectValue placeholder="Choose state" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-72">{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            ) : <F label="State / region (optional)">{inp("state")}</F>}
            <F label={india ? "PIN code" : "Postal code"} error={errors.postal_code}>{inp("postal_code", india ? { inputMode: "numeric", maxLength: 6 } : {})}</F>
            {!india && <F label="Country" error={errors.country}>{inp("country")}</F>}
          </fieldset>
          <fieldset className="grid sm:grid-cols-2 gap-4" data-testid="checkout-billing">
            {me && !me.addresses.some((a) => a.line1 === f.line1 && a.postal_code === f.postal_code) && (
              <label className="sm:col-span-2 flex items-center gap-2.5 text-sm font-arial cursor-pointer -mt-4 mb-2"><Checkbox checked={saveAddr} onCheckedChange={(v) => setSaveAddr(!!v)} className="rounded-none" data-testid="checkout-save-address" /> Save this delivery address to my account</label>
            )}
            <legend className="font-head font-bold text-lg mb-3">Billing address</legend>
            <label className="sm:col-span-2 flex items-center gap-2.5 text-sm font-arial cursor-pointer">
              <Checkbox checked={same} onCheckedChange={(v) => setSame(!!v)} className="rounded-none" data-testid="checkout-billing-same" /> Billing address is the same as the delivery address
            </label>
            {!same && (
              <>
                <div className="sm:col-span-2"><F label="Name on bill" error={errors.b_name}>{binp("name")}</F></div>
                <div className="sm:col-span-2"><F label="Address line 1" error={errors.b_line1}>{binp("line1")}</F></div>
                <div className="sm:col-span-2"><F label="Address line 2 (optional)">{binp("line2")}</F></div>
                <F label="City" error={errors.b_city}>{binp("city")}</F>
                <F label="State (optional)">{binp("state")}</F>
                <F label="PIN / postal code" error={errors.b_postal_code}>{binp("postal_code")}</F>
                <F label="Country" error={errors.b_country}>{binp("country")}</F>
              </>
            )}
          </fieldset>
        </div>
        <aside className="bg-white border border-[#e3e3e3] p-6 h-fit">
          <h3 className="font-head font-bold mb-4">Order summary</h3>
          <ul className="space-y-2 text-sm mb-4">
            {cart.items.map((i) => (
              <li key={i.product_id} className="flex justify-between gap-3"><span className="truncate">{i.snapshot.artist} – {i.snapshot.title} × {i.qty}</span><span className="font-arial">{inr(i.qty * i.snapshot.price)}</span></li>
            ))}
          </ul>
          <CouponBox coupon={coupon} setCoupon={setCoupon} email={f.email} items={cart.items.map((i) => ({ product_id: i.product_id, qty: i.qty }))} />
          <div className="border-t border-[#e5e5e5] pt-3 space-y-1.5 font-arial text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{inr(cart.subtotal)}</span></div>
            {disc > 0 && <div className="flex justify-between text-[#1f7a3a]"><span>Coupon {coupon.code}</span><span data-testid="checkout-discount">−{inr(disc)}</span></div>}
            <div className="flex justify-between"><span>Delivery</span><span data-testid="checkout-shipping">{ship ? inr(ship) : "Free"}</span></div>
            <div className="flex justify-between text-base font-bold pt-2"><span>Total</span><span className="text-red" data-testid="checkout-total">{inr(cart.subtotal - disc + ship)}</span></div>
          </div>
          <label className="mt-6 flex items-start gap-2.5 text-xs leading-relaxed cursor-pointer" data-testid="checkout-terms-label">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#dc1f26] shrink-0" data-testid="checkout-terms-checkbox" />
            <span>I have read and agreed to the <a href="/terms-and-conditions" target="_blank" rel="noreferrer" className="text-red underline">terms and conditions</a>.</span>
          </label>
          {errors.terms && <p className="text-xs text-red mt-1.5" data-testid="checkout-terms-error">{errors.terms}</p>}
          <button disabled={busy || !terms} className="mt-4 w-full bg-black text-white py-3.5 text-xs font-bold tracking-[0.15em] hover:bg-[#dc1f26] disabled:opacity-50 transition-colors" data-testid="checkout-place-order">
            {busy ? "PLACING ORDER…" : "CONTINUE TO PAYMENT"}
          </button>
        </aside>
      </form>
    </div>
  );
}
